// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — State Components (Loading, Empty, Error, Locked)
// ══════════════════════════════════════════════════════════════════════════════

import { Loader, FileText, AlertCircle, Lock, Zap } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Button } from './Primitives';

// ─── Spinner ───────────────────────────────────────────────────────────────
export function LoadingSpinner({ size = 32, fullscreen = false }) {
  const { theme } = useTheme();
  const { t } = useLang();

  const content = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 14,
      padding: 60
    }}>
      <Loader size={size} color={theme.primary} style={{ animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: theme.textSecondary }}>{t.loading}</span>
    </div>
  );

  if (fullscreen) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.bg
      }}>
        {content}
      </div>
    );
  }

  return content;
}

// ─── Empty State ───────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  const { theme } = useTheme();
  const { t } = useLang();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 400,
      textAlign: 'center',
      padding: 40
    }}>
      <div style={{
        width: 80,
        height: 80,
        borderRadius: 20,
        background: theme.primaryDim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20
      }}>
        {icon || <FileText size={36} color={theme.primary} />}
      </div>
      <h3 style={{
        margin: '0 0 8px',
        fontSize: 18,
        fontWeight: 700,
        color: theme.text
      }}>
        {title || t.noDataYet}
      </h3>
      <p style={{
        margin: 0,
        fontSize: 13,
        color: theme.textSecondary,
        maxWidth: 360,
        lineHeight: 1.6
      }}>
        {description || t.noDataDesc}
      </p>
      {action && <div style={{ marginTop: 20 }}>{action}</div>}
    </div>
  );
}

// ─── Error State ───────────────────────────────────────────────────────────
export function ErrorState({ error, onRetry }) {
  const { theme } = useTheme();
  const { t } = useLang();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 400,
      textAlign: 'center',
      padding: 40
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: 20,
        background: theme.dangerDim,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 20
      }}>
        <AlertCircle size={36} color={theme.danger} />
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: theme.text }}>
        {t.errorOccurred}
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: theme.textSecondary, maxWidth: 420 }}>
        {error?.message || error || t.error}
      </p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>{t.retry}</Button>
      )}
    </div>
  );
}

// ─── Locked Module ─────────────────────────────────────────────────────────
export function LockedModule({ moduleTitle }) {
  const { theme } = useTheme();
  const { t } = useLang();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 400,
      textAlign: 'center',
      padding: 40
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: 20,
        background: theme.warningDim,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 20,
        border: `2px solid ${theme.warning}40`
      }}>
        <Lock size={36} color={theme.warning} />
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: theme.text }}>
        {moduleTitle} — {t.locked}
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: theme.textSecondary, maxWidth: 380 }}>
        {t.lockedDesc}
      </p>
      <Button variant="outline">
        <Zap size={14} /> {t.upgradeRequest}
      </Button>
    </div>
  );
}
